import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { Empty, Icon, Menu } from "./ui";

/**
 * Analytics' charts, to the familiar admin's Analytics and the data-visualisation method:
 * one axis, the period in a 2px ink line over a light wash, the period before
 * it as a dashed grey line (texture as well as tone, so it reads without
 * colour), a legend whenever there are two lines, a hairline grid, a
 * crosshair that snaps to the nearest point with a tooltip, arrow keys for the
 * same, and every number as a table. Orange stays off data marks.
 */

export function niceMax(v: number): number {
  if (v <= 0) return 4;
  const mag = 10 ** Math.floor(Math.log10(v));
  const step = [1, 2, 2.5, 5, 10].find((s) => s * mag * 4 >= v) ?? 10;
  return step * mag * 4;
}

export const compactMoney = (v: number) => (Math.abs(v) >= 1000 ? `$${(v / 1000).toLocaleString("en-AU", { maximumFractionDigits: 1 })}K` : `$${v.toLocaleString("en-AU", { maximumFractionDigits: 0 })}`);
export const compactNum = (v: number) => (Math.abs(v) >= 1000 ? `${(v / 1000).toLocaleString("en-AU", { maximumFractionDigits: 1 })}K` : v.toLocaleString("en-AU", { maximumFractionDigits: 1 }));
export const pct = (a: number, b: number, digits = 1) => (b ? `${((a / b) * 100).toLocaleString("en-AU", { maximumFractionDigits: digits })}%` : "—");

/** A figure against the period before it, in words and an arrow, never colour alone. */
export function Delta({ now, before, downIsGood = false, points = false, amount, label = "vs previous" }: { now: number; before: number | undefined; downIsGood?: boolean; points?: boolean; /** Say the change as an amount (money) instead of a percentage: for figures that can cross nought, such as profit. */ amount?: (n: number) => string; label?: string }) {
  if (before === undefined) return null;
  let text: string;
  let dir = 0;
  if (amount) {
    const d = now - before;
    dir = Math.sign(Math.round(d * 100));
    text = dir === 0 ? "No change" : `${dir > 0 ? "▲" : "▼"} ${amount(Math.abs(d))}`;
  } else if (points) {
    const d = now - before;
    dir = Math.sign(Math.round(d * 10));
    text = dir === 0 ? "No change" : `${dir > 0 ? "▲" : "▼"} ${Math.abs(d).toLocaleString("en-AU", { maximumFractionDigits: 1 })} pts`;
  } else if (before === 0) {
    text = now === 0 ? "No change" : "New this period";
    dir = now === 0 ? 0 : 1;
  } else {
    const c = ((now - before) / Math.abs(before)) * 100;
    dir = Math.round(c) === 0 ? 0 : Math.sign(c);
    text = dir === 0 ? "No change" : `${dir > 0 ? "▲" : "▼"} ${Math.abs(c).toLocaleString("en-AU", { maximumFractionDigits: 0 })}%`;
  }
  const tone = dir === 0 || text === "New this period" ? "" : (dir > 0) !== downIsGood ? " is-good" : " is-bad";
  return (
    <em className={`stat__delta${tone}`}>
      {text}
      <span className="muted"> {label}</span>
    </em>
  );
}

function useWidth(min = 240, initial = 560) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(initial);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (e) setWidth(Math.max(min, Math.round(e.contentRect.width)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [min]);
  return [ref, width] as const;
}

export interface Series {
  label: string;
  values: number[];
  /** The period before: dashed, grey, drawn under the current line. */
  previous?: boolean;
}

/**
 * A line over time. The first series is the period; an optional second
 * (previous: true) is the one before it, aligned point by point.
 */
export function LineChart({
  series,
  labels,
  long,
  format,
  tick,
  unit,
  height = 220,
  refreshing,
  table,
  empty = "Nothing in this period",
}: {
  series: Series[];
  /** Short labels under the axis, one per point. */
  labels: string[];
  /** Each point's full name, for the tooltip and the table (series index for the previous period's own dates). */
  long: (i: number, s: number) => string;
  format: (v: number) => string;
  tick?: (v: number) => string;
  unit: string;
  height?: number;
  refreshing?: boolean;
  table?: { head: string; cols?: { label: string; value: (i: number) => ReactNode }[] };
  empty?: string;
}) {
  const [wrap, width] = useWidth();
  const [at, setAt] = useState<number | null>(null);
  const cur = series.find((s) => !s.previous) ?? series[0];
  const prev = series.find((s) => s.previous);
  const n = cur?.values.length ?? 0;
  useEffect(() => setAt(null), [n]);
  if (!cur || !n) return <Empty title={empty} />;
  const H = height;
  const L = 48;
  const R = 12;
  const T = 12;
  const B = 26;
  const all = [...cur.values, ...(prev?.values ?? [])];
  const max = niceMax(Math.max(0, ...all));
  const x = (i: number) => L + (n <= 1 ? (width - L - R) / 2 : (i * (width - L - R)) / (n - 1));
  const y = (v: number) => T + (H - T - B) * (1 - v / max);
  const path = (vals: number[]) => vals.slice(0, n).map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join("");
  const line = path(cur.values);
  const area = `${line}L${x(n - 1).toFixed(1)},${y(0)}L${x(0).toFixed(1)},${y(0)}Z`;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const xTicks = n <= 1 ? [0] : n < 5 ? [0, n - 1] : [0, Math.floor((n - 1) / 2), n - 1];
  const fmtTick = tick ?? format;
  const move = (e: PointerEvent<SVGSVGElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - box.left) / box.width) * width;
    const i = n <= 1 ? 0 : Math.round(((px - L) / (width - L - R)) * (n - 1));
    setAt(Math.max(0, Math.min(n - 1, i)));
  };
  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      setAt((a) => Math.max(0, Math.min(n - 1, (a ?? (e.key === "ArrowRight" ? -1 : n)) + (e.key === "ArrowRight" ? 1 : -1))));
    }
    if (e.key === "Escape") setAt(null);
  };
  const total = cur.values.reduce((s, v) => s + v, 0);
  const prevAt = at !== null && prev ? prev.values[at] : undefined;
  return (
    <>
      {prev && (
        <ul className="legend" aria-label="Lines">
          <li>
            <i className="legend__line" aria-hidden="true" />
            {cur.label}
          </li>
          <li>
            <i className="legend__line legend__line--previous" aria-hidden="true" />
            {prev.label}
          </li>
        </ul>
      )}
      <div
        ref={wrap}
        className={`chart${refreshing ? " is-refreshing" : ""}`}
        tabIndex={0}
        role="img"
        aria-label={`${unit}: ${format(total)} in total across ${n} points${prev ? `, against ${format(prev.values.reduce((s, v) => s + v, 0))} in the period before` : ""}. Use the arrow keys to read each point.`}
        onKeyDown={key}
        onBlur={() => setAt(null)}
      >
        <svg viewBox={`0 0 ${width} ${H}`} width={width} height={H} onPointerMove={move} onPointerLeave={() => setAt(null)} aria-hidden="true">
          {ticks.map((v) => (
            <g key={v}>
              <line x1={L} x2={width - R} y1={y(v)} y2={y(v)} className="chart__grid" />
              <text x={L - 8} y={y(v)} className="chart__tick" textAnchor="end" dominantBaseline="middle">
                {fmtTick(v)}
              </text>
            </g>
          ))}
          {xTicks.map((i) => (
            <text key={i} x={x(i)} y={H - 6} className="chart__tick" textAnchor={i === 0 && n > 1 ? "start" : i === n - 1 && n > 1 ? "end" : "middle"}>
              {labels[i] ?? ""}
            </text>
          ))}
          {prev && <path d={path(prev.values)} className="chart__line chart__line--previous" />}
          <path d={area} className="chart__area" />
          <path d={line} className="chart__line" />
          {n === 1 && <circle cx={x(0)} cy={y(cur.values[0] ?? 0)} r={4} className="chart__dot" />}
          {at !== null && (
            <g>
              <line x1={x(at)} x2={x(at)} y1={T} y2={y(0)} className="chart__cross" />
              {prevAt !== undefined && <circle cx={x(at)} cy={y(prevAt)} r={3.5} className="chart__dot chart__dot--previous" />}
              <circle cx={x(at)} cy={y(cur.values[at] ?? 0)} r={4} className="chart__dot" />
            </g>
          )}
        </svg>
        {at !== null && (
          <div className="chart__tip" style={{ left: `${Math.min(88, Math.max(12, (x(at) / width) * 100))}%`, top: Math.min(y(cur.values[at] ?? 0), prevAt !== undefined ? y(prevAt) : Infinity) - 10 }}>
            <b>{format(cur.values[at] ?? 0)}</b>
            <span>{long(at, 0)}</span>
            {prevAt !== undefined && (
              <span className="chart__tip-prev">
                {format(prevAt)} · {long(at, 1)}
              </span>
            )}
          </div>
        )}
      </div>
      {table !== undefined && (
        <details className="chart__table">
          <summary>Show as a table</summary>
          <table className="table">
            <thead>
              <tr>
                <th>{table.head}</th>
                {(table.cols ?? []).map((c) => (
                  <th key={c.label} className="num">
                    {c.label}
                  </th>
                ))}
                <th className="num">{cur.label}</th>
                {prev && <th className="num">{prev.label}</th>}
              </tr>
            </thead>
            <tbody>
              {cur.values.map((v, i) => (
                <tr key={i}>
                  <td>{long(i, 0)}</td>
                  {(table.cols ?? []).map((c) => (
                    <td key={c.label} className="num">
                      {c.value(i)}
                    </td>
                  ))}
                  <td className="num">{format(v)}</td>
                  {prev && <td className="num">{prev.values[i] !== undefined ? format(prev.values[i] ?? 0) : "—"}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </>
  );
}

/** A tile's small line: the period in ink, the one before dashed under it. No axis; the tile states the figure. */
export function Sparkline({ values, previous, label }: { values: number[]; previous?: number[]; label: string }) {
  const w = 120;
  const h = 32;
  const n = values.length;
  if (n < 2) return <svg className="spark" viewBox={`0 0 ${w} ${h}`} aria-hidden="true" />;
  const max = Math.max(1e-9, ...values, ...(previous ?? []));
  const x = (i: number) => 1 + (i * (w - 2)) / (n - 1);
  const y = (v: number) => h - 2 - (v / max) * (h - 4);
  const d = (vals: number[]) => vals.slice(0, n).map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join("");
  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" role="img" aria-label={label}>
      {previous && previous.length > 1 && <path d={d(previous)} className="spark__previous" />}
      <path d={d(values)} className="spark__line" />
    </svg>
  );
}

export interface StripMetric {
  label: string;
  value: ReactNode;
  /** Each day's figure, for the small line. */
  spark?: number[];
}

export const STRIP_RANGES = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
];

/**
 * The strip of figures above a list (orders, say), the familiar admin's: the period on the left as a pill that
 * changes it, then each figure with its label and a small line of its days. Scrolls sideways on a phone.
 */
export function MetricsStrip({ days, onDays, metrics, label }: { days: number; onDays: (days: number) => void; metrics: StripMetric[] | null; label: string }) {
  const range = STRIP_RANGES.find((r) => r.days === days) ?? STRIP_RANGES[1]!;
  return (
    <section className="metrics-strip" aria-label={label}>
      <div className="metrics-strip__range">
        <Menu
          label={range.label}
          icon={<Icon.run />}
          ariaLabel={`${range.label}. Change the period`}
          items={STRIP_RANGES.map((r) => ({ key: String(r.days), label: r.label, onSelect: () => onDays(r.days) }))}
        />
      </div>
      <div className="metrics-strip__items">
        {(metrics ?? []).map((m) => (
          <div key={m.label} className="metrics-strip__item">
            <small>{m.label}</small>
            <span className="metrics-strip__row">
              <b>{m.value}</b>
              {m.spark && m.spark.length > 1 && <Sparkline values={m.spark} label={`${m.label} each day`} />}
            </span>
          </div>
        ))}
        {!metrics && <div className="metrics-strip__item metrics-strip__item--wait"><small>Loading…</small></div>}
      </div>
    </section>
  );
}

/** A KPI tile, the familiar admin's: the label, the figure, its change, and a small line. */
export function KpiTile({ label, value, delta, spark, to }: { label: string; value: ReactNode; delta?: ReactNode; spark?: ReactNode; to?: string }) {
  const body = (
    <>
      <span>{label}</span>
      <b>{value}</b>
      {delta}
      {spark}
    </>
  );
  return <div className="stat stat--kpi">{to ? <Link to={to}>{body}</Link> : body}</div>;
}

export interface BarRow {
  key: string;
  label: ReactNode;
  value: number;
  /** What the figure says, if not the share. */
  text?: ReactNode;
  title?: string;
  onClick?: () => void;
  /** The bar's colour, when not ink (a Loops level's colour, say). */
  color?: string;
}

/** Shares as ink bars (the design system's .bars), largest first; a row can be a button. */
export function BarList({ rows, total, empty = "Nothing yet.", wide }: { rows: BarRow[]; total?: number; empty?: string; wide?: boolean }) {
  if (!rows.length) return <p className="muted">{empty}</p>;
  const sum = total ?? rows.reduce((s, r) => s + r.value, 0);
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className={`bars${wide ? " bars--wide" : ""}`}>
      {rows.map((r) => {
        const inner = (
          <>
            <span title={r.title}>{r.label}</span>
            <i style={{ width: `${Math.max(1, Math.round((r.value / max) * 100))}%`, ...(r.color ? { background: r.color } : {}) }} />
            <b>{r.text ?? pct(r.value, sum, 0)}</b>
          </>
        );
        return (
          <li key={r.key} className={r.onClick ? "is-button" : undefined}>
            {r.onClick ? (
              <button type="button" className="bars__btn" onClick={r.onClick}>
                {inner}
              </button>
            ) : (
              inner
            )}
          </li>
        );
      })}
    </ul>
  );
}

/* ---------------------------------------------------------------- places */

const REGION_SHORT: Record<string, string> = {
  "New South Wales": "NSW",
  Victoria: "VIC",
  Queensland: "QLD",
  "South Australia": "SA",
  "Western Australia": "WA",
  Tasmania: "TAS",
  "Northern Territory": "NT",
  "Australian Capital Territory": "ACT",
};

let regionNames: Intl.DisplayNames | null = null;
export function countryName(cc: string | null | undefined): string {
  if (!cc) return "Unknown";
  try {
    regionNames ??= new Intl.DisplayNames(["en-AU"], { type: "region" });
    return regionNames.of(cc.toUpperCase()) ?? cc;
  } catch {
    return cc;
  }
}

export interface PlaceLike {
  country?: string | null;
  region?: string | null;
  city?: string | null;
  geo?: string | null;
}

/** "Sydney, NSW" in Australia, "Los Angeles, United States" elsewhere; the country alone without a city. */
export function placeText(p: PlaceLike): string {
  if (!p.country) return "Unknown";
  if (p.country === "AU") {
    const r = p.region ? (REGION_SHORT[p.region] ?? p.region) : "";
    return p.city ? `${p.city}${r ? `, ${r}` : ""}` : r || "Australia";
  }
  return p.city ? `${p.city}, ${countryName(p.country)}` : countryName(p.country);
}

export function regionText(p: PlaceLike): string {
  if (!p.country) return "Unknown";
  if (!p.region) return countryName(p.country);
  return p.country === "AU" ? p.region : `${p.region}, ${countryName(p.country)}`;
}

export const APPROX = "Approximate: from the device's time zone, for visits before places were recorded by address";
